import { HttpHandlerFn, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { isPrimaryRequest, ACCEPT_LANGUAGE } from './accept-language.context';
import { TALBINAH_TOKEN, TALBINAH_DEVICE_HEADERS } from './http.service';
import { Language } from '@core/i18n/domain/language';

/**
 * headersInterceptor — v2's own copy of the app-wide headers the legacy
 * `headers.interceptor.ts` (common/core/data-access/http-client/interceptors)
 * currently attaches to EVERY request, v1 and v2 alike (that file has no v2
 * skip-branch, unlike auth/error). Added per docs/v2-isolation/ESCALATIONS.md
 * E1: `common/` can't be deleted while v2 depends on the legacy interceptor
 * running for it, so v2 needs its own equivalent BEFORE that deletion happens.
 *
 * Registered ALONGSIDE the legacy interceptor for now (both fire for v2
 * traffic — idempotent, since both compute the same header values from the
 * same request context, so no behaviour changes today). Once
 * `common/` is deleted in a later phase, this is the only one left and v2's
 * HTTP headers keep working unchanged.
 *
 * Stripped from the legacy version: the `else` branches that read
 * `TranslationFacade`/`StorageKeys.LANGUAGE` for the language fallback are
 * v1-only in practice for v2 traffic — every v2 request already carries
 * {@link ACCEPT_LANGUAGE} (set by `AppHttp` via `withLanguage`), so that
 * fallback path is unreachable for v2 today and is not replicated.
 */
export function headersInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (!isPrimaryRequest(req)) return next(req);

  const requestLang = req.context.get(ACCEPT_LANGUAGE);
  const acceptLanguage = requestLang === Language.Ar ? Language.Ar : Language.En;
  const b2bValue = getB2BValue();

  let headers = Object.entries(TALBINAH_DEVICE_HEADERS).reduce(
    (h, [name, value]) => h.set(name, value),
    req.headers.set('Accept-Language', acceptLanguage).set('talbinah-token', TALBINAH_TOKEN),
  );

  if (b2bValue) {
    headers = headers.set(B2B_PARAM, b2bValue);
  }

  return next(req.clone({ headers }));
}

// ── b2b passthrough (SSR-safe cookie/URL-param read) ────────────────────────
// Adapted from common/core/data-access/http-client/utils/cookie.utils.ts —
// copied rather than imported so this interceptor has zero legacy imports.

/** The header / cookie / URL query-param name the b2b passthrough is keyed on. */
const B2B_PARAM = 'b2b';

function setB2BCookie(value: string): void {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000); // 1 day
  document.cookie = `${B2B_PARAM}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function getB2BCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = (document.cookie || '').match(new RegExp(`(^| )${B2B_PARAM}=([^;]+)`));
  return match ? decodeURIComponent(match[2]!) : null;
}

function getB2BValue(): string | null {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const urlParam = url.searchParams.get(B2B_PARAM);
    if (urlParam) {
      setB2BCookie(urlParam);
      url.searchParams.delete(B2B_PARAM);
      window.history.replaceState({}, '', url.toString());
      return urlParam;
    }
  }
  return getB2BCookie();
}
